import {
  DateTimePicker,
  HorizontalDivider,
  Image,
  Input,
  Toggle,
  Typography
} from '@smartgift/frontend-toolkit';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Section } from '@hero/atoms';
import { OverviewAdvanceSettings } from '@hero/components/organisms';
import { configs, constants } from '@hero/configs';
import {
  dateLabelFormat,
  NON_EDITABLE_RECOGNITION_STATUS,
  PAYMENT_METHOD_TYPES,
  MAX_FEATURE_NAME_LENGTH,
  SOURCE_TYPES,
  MAX_PO_LENGTH,
  MAX_COST_CENTER_LENGTH
} from '@hero/constants';
import { DetailItem } from '@hero/molecules';
import {
  useGetRecognition,
  useUpdateRecognition
} from '@hero/services/order-service/recognition/hooks';
import { useGetUser } from '@hero/services/organization-service/hooks';
import { cleanObjectEmptyFields, getName } from '@hero/utils';
import { formatDate } from '@hero/utils/helper';

import { withMerchantComponents } from '../../../../../../utils/merchantUtils';

import * as S from './style';

export const Overview = () => {
  const { recognitionCode } = useParams();
  const { t } = useTranslation();
  const { data: recognition } = useGetRecognition({ recognitionCode });
  const { mutateAsync: updateRecognition, isLoading: isLoadingUpdateRecognition } =
    useUpdateRecognition();
  const { data: user } = useGetUser();

  const [poNumber, setPoNumber] = useState();
  const [costCenter, setCostCenter] = useState();
  const [expirationDate, setExpirationDate] = useState();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState();
  const [recognitionName, setRecognitionName] = useState();
  const [settings, setSettings] = useState();
  const [advanceSettings, setAdvanceSettings] = useState({
    settings: {
      onlyCollectAddress: false,
      charityRules: {
        enabled: false,
        selectedCharities: []
      },
      disableScratchEffect: false,
      receiveThankYouNotes: false
    }
  });

  const messages = {
    requireMeeting: t('requireMeetingDesc'),
    itemReGift: t('itemReGiftDesc'),
    premiumDates: t('premiumDatesDesc')
  };

  const isDetailItemEditable = React.useMemo(
    () => !NON_EDITABLE_RECOGNITION_STATUS.includes(recognition?.status?.current),
    [recognition]
  );

  const hasMultipleApps = constants.apps?.length > 1;
  const source = constants.apps.find(app => app.id === recognition?.source);

  useEffect(() => {
    if (recognition?.name) {
      setRecognitionName(recognition.name);
      setPoNumber(recognition.meta?.poNumber);
      setCostCenter(recognition.meta?.costCenter);
      setSelectedPaymentMethod(recognition.paymentMethod);
      setExpirationDate(recognition.settings?.expiresAt);

      setSettings({
        disableReGifting: recognition.settings?.disableReGifting,
        allowPremiumDates: recognition.settings?.allowPremiumDates,
        redeemRules: recognition.settings?.redeemRules
      });

      setAdvanceSettings({
        settings: {
          onlyCollectAddress: recognition.settings?.onlyCollectAddress,
          charityRules: recognition.settings?.charityRules,
          disableScratchEffect: recognition.settings?.disableScratchEffect,
          receiveThankYouNotes: recognition.settings?.receiveThankYouNotes
        }
      });
    }
  }, [recognition]);

  const isCCPayment = recognition?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard;

  const advanceSettingsEnabled = useMemo(() => {
    return (
      configs?.advanceSettings?.enabled &&
      recognition?.source !== SOURCE_TYPES.teams_app &&
      (configs?.advanceSettings?.reGiftingEnabled ||
        configs?.advanceSettings?.charityEnabled ||
        configs?.advanceSettings?.premiumDates ||
        configs?.advanceSettings?.onlyCollectAddressEnabled ||
        configs?.advanceSettings?.scratchEffectEnabled)
    );
  }, [configs?.advanceSettings]);

  return (
    <S.OverviewWrapper>
      {hasMultipleApps && (
        <S.ImageWrapper>
          <Image name={source?.image} width={32} />
        </S.ImageWrapper>
      )}
      <DetailItem title={`${t('orderDate')}:`}>
        {formatDate(recognition?.createdAt, { includeTime: false })}
      </DetailItem>
      {configs.paymentMethods.enabled && (
        <DetailItem title={`${t('paymentMethod')}:`}>
          {selectedPaymentMethod?.type === PAYMENT_METHOD_TYPES.houseAccount
            ? t('houseAccount')
            : selectedPaymentMethod?.name}
        </DetailItem>
      )}
      <DetailItem title={`${t('giftCode')}:`}>{recognition?.code}</DetailItem>
      <DetailItem title={`${t('senderName')}:`}>
        {getName({
          firstName: recognition?.createdBy?.firstName,
          lastName: recognition?.createdBy?.lastName
        })}
      </DetailItem>
      <DetailItem
        withBorder
        bodyStyle={{
          paddingBottom: 'var(--size-16)',
          marginBottom: 'var(--size-8)'
        }}
        title={`${t('source')}:`}
      >
        {source?.name}
      </DetailItem>
      <DetailItem
        editable={isDetailItemEditable}
        bodyStyle={{ alignItems: 'center' }}
        title={`${t('giftName')}:`}
        closeWithoutSave={() => {
          setRecognitionName(recognition?.name);
        }}
        saveDetail={() => {
          updateRecognition({
            params: { recognitionCode },
            body: {
              name: recognitionName
            }
          });
        }}
        editInput={() => (
          <Input
            value={recognitionName}
            inputProps={{
              onChange: e => setRecognitionName(e.target.value),
              maxLength: MAX_FEATURE_NAME_LENGTH
            }}
            formItem={{
              label: t('recognitionNameLabel'),
              name: 'recognition_name',
              validations: {
                required: {
                  required: true
                }
              }
            }}
          />
        )}
      >
        {recognitionName}
      </DetailItem>
      <DetailItem
        key={recognition?.settings?.expiresAt}
        title={`${t('redemptionDeadline')}:`}
        bodyStyle={{ alignItems: 'center' }}
        editable={isDetailItemEditable}
        closeWithoutSave={() => {
          setExpirationDate(recognition?.settings?.expiresAt);
        }}
        hasActionButtons={false}
        editInput={() => (
          <DateTimePicker
            dateFormatWithTime="EEE, LLL d - hh:mm aa"
            placeholder={t('endDate')}
            value={expirationDate ? new Date(expirationDate) : null}
            onChange={date => {
              setExpirationDate(date.toISOString());
              updateRecognition({
                params: { recognitionCode },
                body: {
                  settings: {
                    expiresAt: expirationDate
                  }
                }
              });
            }}
            popperPlacement="bottom-start"
            showTimeSelect
            excludePast
            {...(isCCPayment && { maxDate: new Date().setDate(new Date().getDate() + 90) })}
          />
        )}
      >
        <span>
          {expirationDate
            ? formatDate(expirationDate, {
                format: dateLabelFormat,
                includeTime: true
              })
            : t('notSet')}
        </span>
      </DetailItem>
      {(recognition?.source !== SOURCE_TYPES.teams_app || !user?.meta?.isPunchoutUser) && (
        <>
          <DetailItem
            editable={isDetailItemEditable}
            title={`${t('po')}#:`}
            bodyStyle={{ alignItems: 'center' }}
            closeWithoutSave={() => {
              setPoNumber(recognition?.meta?.poNumber);
            }}
            saveDetail={() => {
              updateRecognition({
                params: { recognitionCode },
                body: {
                  meta: {
                    poNumber,
                    costCenter
                  }
                }
              });
            }}
            editInput={() => (
              <Input
                value={poNumber}
                inputProps={{
                  maxLength: MAX_PO_LENGTH,
                  onChange: e => {
                    setPoNumber(e.target.value);
                  }
                }}
                formItem={{
                  label: t('poLabel'),
                  name: 'po'
                }}
              />
            )}
          >
            {poNumber || t('notSet')}
          </DetailItem>
          <DetailItem
            editable={isDetailItemEditable}
            bodyStyle={{ alignItems: 'center' }}
            closeWithoutSave={() => {
              setCostCenter(recognition?.meta?.costCenter);
            }}
            saveDetail={() => {
              updateRecognition({
                params: { recognitionCode },
                body: {
                  meta: {
                    poNumber,
                    costCenter
                  }
                }
              });
            }}
            editInput={() => (
              <Input
                value={costCenter}
                inputProps={{
                  maxLength: MAX_COST_CENTER_LENGTH,
                  onChange: e => {
                    setCostCenter(e.target.value);
                  }
                }}
                formItem={{
                  label: t('costCenterLabel'),
                  name: 'costCenter'
                }}
              />
            )}
            title={`${t('costCenter')}:`}
          >
            {costCenter || t('notSet')}
          </DetailItem>
        </>
      )}

      {recognition?.source !== 'teams_app' && advanceSettingsEnabled && (
        <Section className="section-settings">
          <Typography variant="body1Bold" style={{ marginBottom: 'var(--size-8)' }}>
            {t('settings')}
          </Typography>
          {configs?.advanceSettings?.reGiftingEnabled && (
            <DetailItem title={`${t('itemReGifting')}:`} className="item-d-flex">
              <Typography variant="body2" color="neutral" emphasis="500">
                {messages.itemReGift}
              </Typography>
              <Toggle
                checked={!settings?.disableReGifting}
                disabled={!isDetailItemEditable || isLoadingUpdateRecognition}
                onChange={async () => {
                  const newSettings = {
                    ...recognition?.settings,
                    expiresAt: expirationDate,
                    disableReGifting: !settings?.disableReGifting
                  };

                  updateRecognition({
                    params: { recognitionCode },
                    body: {
                      settings: {
                        disableReGifting: !settings?.disableReGifting
                      }
                    }
                  });

                  await setSettings(newSettings);
                }}
              />
            </DetailItem>
          )}
          {configs.advanceSettings.premiumDates !== false && (
            <>
              {configs.advanceSettings.reGiftingEnabled && <HorizontalDivider $margin="8px 0" />}
              <DetailItem title={`${t('premiumDates')}:`} className="item-d-flex">
                <p>{messages.premiumDates}</p>
                <Toggle
                  checked={settings?.allowPremiumDates}
                  disabled={!isDetailItemEditable || isLoadingUpdateRecognition}
                  onChange={async () => {
                    const newSettings = {
                      ...recognition?.settings,
                      expiresAt: expirationDate,
                      allowPremiumDates: !settings?.allowPremiumDates
                    };

                    updateRecognition({
                      params: {
                        recognitionCode
                      },
                      body: {
                        settings: {
                          allowPremiumDates: !settings?.allowPremiumDates
                        }
                      }
                    });

                    await setSettings(newSettings);
                  }}
                />
              </DetailItem>
            </>
          )}
          {configs.advanceSettings.enabled && (
            <>
              <HorizontalDivider $margin="16px 0" />
              <OverviewAdvanceSettings
                advanceSettings={advanceSettings}
                disabled={!isDetailItemEditable || isLoadingUpdateRecognition}
                setAdvanceSettings={({
                  settings: {
                    charityRules: { enabled, selectedCharities } = {},
                    onlyCollectAddress,
                    disableScratchEffect,
                    receiveThankYouNotes
                  }
                }) => {
                  setAdvanceSettings({
                    settings: {
                      charityRules: { enabled, selectedCharities },
                      onlyCollectAddress,
                      disableScratchEffect,
                      receiveThankYouNotes
                    }
                  });
                  const body = {
                    settings: cleanObjectEmptyFields({
                      charityRules: cleanObjectEmptyFields({
                        enabled,
                        selectedCharities: selectedCharities?.length ? selectedCharities : undefined
                      }),
                      disableScratchEffect,
                      receiveThankYouNotes
                    })
                  };

                  updateRecognition({
                    params: {
                      recognitionCode: recognition?.code
                    },
                    body
                  });
                }}
              />
            </>
          )}
        </Section>
      )}
    </S.OverviewWrapper>
  );
};

const RecognitionDetailOverviewAccordion = withMerchantComponents({
  Component: Overview,
  fileName: 'RecognitionDetailOverviewAccordion',
  atomicFolderName: 'organisms'
});

export { RecognitionDetailOverviewAccordion };
